import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, RefreshCw, Search, UploadCloud } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { RegistrationRow } from '../types';

const PAGE_SIZE = 10;

type SortDirection = 'asc' | 'desc';
type SortKey =
  | 'lastName'
  | 'firstName'
  | 'emailId'
  | 'registrationCode'
  | 'registrationStatus'
  | 'attendeeType'
  | 'business'
  | 'roomsRequested';

interface ColumnDef {
  key: SortKey;
  label: string;
  widthClass: string;
}

interface RegistrationsTableProps {
  registrations: RegistrationRow[];
  selectedRegistrationCodes: Set<string>;
  isLoading: boolean;
  isError: boolean;
  isReprocessing: boolean;
  onToggleRegistration: (registrationCode: string, selected: boolean) => void;
  onToggleAllRegistrations: (registrationCodes: string[], selected: boolean) => void;
  onOpenPullRegistrations: () => void;
  onReprocessSelected: () => void;
}

const columns: ColumnDef[] = [
  { key: 'lastName', label: 'Last Name', widthClass: 'min-w-36' },
  { key: 'firstName', label: 'First Name', widthClass: 'min-w-36' },
  { key: 'emailId', label: 'Email', widthClass: 'min-w-56' },
  { key: 'registrationCode', label: 'Registration Id', widthClass: 'min-w-44' },
  { key: 'registrationStatus', label: 'Reg Status', widthClass: 'min-w-44' },
  { key: 'attendeeType', label: 'Attendee Type', widthClass: 'min-w-48' },
  { key: 'business', label: 'Business', widthClass: 'min-w-44' },
  { key: 'roomsRequested', label: 'Rooms Requested', widthClass: 'min-w-44' },
];

function getCellValue(row: RegistrationRow, key: SortKey): string | number {
  const value = row[key];
  if (typeof value === 'number') return value;
  return value ?? '';
}

function formatCellValue(value: string | number): string {
  if (typeof value === 'number') return String(value);
  return value || '-';
}

function getRegistrationCode(row: RegistrationRow): string | null {
  const code = row.registrationCode?.trim();
  return code ? code : null;
}

interface HeaderCheckboxProps {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}

function HeaderCheckbox({ checked, indeterminate, disabled, onChange }: HeaderCheckboxProps) {
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={checkboxRef}
      type="checkbox"
      aria-label="Select all registrations"
      className="h-4 w-4 border-cool-gray-7 text-dds-blue focus:ring-dds-blue"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
    />
  );
}

export function RegistrationsTable({
  registrations,
  selectedRegistrationCodes,
  isLoading,
  isError,
  isReprocessing,
  onToggleRegistration,
  onToggleAllRegistrations,
  onOpenPullRegistrations,
  onReprocessSelected,
}: RegistrationsTableProps) {
  const [searchValue, setSearchValue] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('lastName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);

  const filteredRows = useMemo(() => {
    const trimmedSearch = searchValue.trim().toLowerCase();
    const rows = trimmedSearch
      ? registrations.filter((row) => row.registrationCode?.toLowerCase().includes(trimmedSearch))
      : registrations;

    return [...rows].sort((left, right) => {
      const leftValue = getCellValue(left, sortKey);
      const rightValue = getCellValue(right, sortKey);
      const direction = sortDirection === 'asc' ? 1 : -1;
      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return (leftValue - rightValue) * direction;
      }
      return (
        String(leftValue).localeCompare(String(rightValue), undefined, { sensitivity: 'base' }) *
        direction
      );
    });
  }, [registrations, searchValue, sortDirection, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visibleRows = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const allRegistrationCodes = registrations
    .map(getRegistrationCode)
    .filter((code): code is string => Boolean(code));
  const allRegistrationsSelected =
    allRegistrationCodes.length > 0 &&
    allRegistrationCodes.every((code) => selectedRegistrationCodes.has(code));
  const someRegistrationsSelected = allRegistrationCodes.some((code) =>
    selectedRegistrationCodes.has(code),
  );

  function updateSearch(value: string) {
    setSearchValue(value);
    setPage(1);
  }

  function updateSort(nextSortKey: SortKey) {
    if (sortKey === nextSortKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(nextSortKey);
      setSortDirection('asc');
    }
    setPage(1);
  }

  return (
    <section className="space-y-3" aria-labelledby="registrations-heading">
      <div className="flex flex-col gap-3 border-b border-cool-gray-2 pb-3 lg:flex-row lg:items-center lg:justify-between">
        <h2 id="registrations-heading" className="text-lg font-bold text-black">
          Registrations
        </h2>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          <button
            type="button"
            className="inline-flex h-9 items-center justify-center gap-2 bg-dds-blue px-4 text-sm font-semibold text-white hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={onOpenPullRegistrations}
            disabled={isLoading || isReprocessing}
          >
            <UploadCloud aria-hidden="true" className="h-4 w-4" />
            Pull Registrations
          </button>
          <button
            type="button"
            className="inline-flex h-9 items-center justify-center gap-2 bg-dds-blue px-4 text-sm font-semibold text-white hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={onReprocessSelected}
            disabled={isLoading || isReprocessing}
          >
            {isReprocessing ? (
              <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw aria-hidden="true" className="h-4 w-4" />
            )}
            Reprocess
          </button>
          <div className="relative">
            <label htmlFor="registration-search" className="sr-only">
              Search by Registration Code
            </label>
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-cool-gray-11"
            />
            <input
              id="registration-search"
              type="search"
              value={searchValue}
              onChange={(event) => updateSearch(event.target.value)}
              placeholder="Search by Registration Code..."
              className="h-9 w-full min-w-64 border-cool-gray-7 pl-9 text-sm focus:border-dds-blue focus:ring-dds-blue sm:w-72"
            />
          </div>
        </div>
      </div>

      {isError ? (
        <div role="alert" className="border border-dds-red bg-red-50 p-3 text-sm text-red-700">
          Failed to load registrations. Please try again.
        </div>
      ) : (
        <div className="overflow-x-auto border border-cool-gray-2">
          <table
            className="min-w-full divide-y divide-cool-gray-2 text-left text-sm"
            aria-busy={isLoading}
          >
            <caption className="sr-only">Registration details for the selected event</caption>
            <thead className="bg-gray-50 text-xs font-bold uppercase text-cool-gray-11">
              <tr>
                <th scope="col" className="w-12 px-3 py-3">
                  <HeaderCheckbox
                    checked={allRegistrationsSelected}
                    indeterminate={someRegistrationsSelected && !allRegistrationsSelected}
                    disabled={allRegistrationCodes.length === 0 || isLoading}
                    onChange={(checked) => onToggleAllRegistrations(allRegistrationCodes, checked)}
                  />
                </th>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    aria-sort={
                      sortKey === column.key
                        ? sortDirection === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                    className={cn('px-3 py-3', column.widthClass)}
                  >
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-left font-bold hover:text-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                      onClick={() => updateSort(column.key)}
                    >
                      {column.label}
                      {sortKey === column.key ? (
                        sortDirection === 'asc' ? (
                          <ChevronUp aria-hidden="true" className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
                        )
                      ) : null}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-cool-gray-2 bg-white">
              {isLoading ? (
                Array.from({ length: 5 }, (_, index) => (
                  <tr key={index}>
                    <td className="px-3 py-3" colSpan={columns.length + 1}>
                      <div className="h-4 w-full animate-pulse bg-gray-200" />
                    </td>
                  </tr>
                ))
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td
                    className="px-3 py-6 text-center text-cool-gray-11"
                    colSpan={columns.length + 1}
                  >
                    No registrations found.
                  </td>
                </tr>
              ) : (
                visibleRows.map((row) => {
                  const registrationCode = getRegistrationCode(row);
                  const rowKey = row.registrationUniqId || registrationCode || `${row.emailId}-${row.firstName}`;
                  return (
                    <tr key={rowKey} className="hover:bg-gray-50">
                      <td className="px-3 py-3 align-top">
                        <input
                          type="checkbox"
                          aria-label={`Select registration ${registrationCode ?? 'without code'}`}
                          className="h-4 w-4 border-cool-gray-7 text-dds-blue focus:ring-dds-blue disabled:cursor-not-allowed disabled:opacity-60"
                          checked={registrationCode ? selectedRegistrationCodes.has(registrationCode) : false}
                          disabled={!registrationCode}
                          onChange={(event) => {
                            if (registrationCode) onToggleRegistration(registrationCode, event.target.checked);
                          }}
                        />
                      </td>
                      {columns.map((column) => (
                        <td key={column.key} className="px-3 py-3 align-top text-black">
                          <span className="block max-w-80 break-words">
                            {formatCellValue(getCellValue(row, column.key))}
                          </span>
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-col gap-2 text-sm text-cool-gray-11 sm:flex-row sm:items-center sm:justify-between">
        <p>
          Showing {filteredRows.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1}-
          {Math.min(safePage * PAGE_SIZE, filteredRows.length)} of {filteredRows.length}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="h-8 border border-cool-gray-7 px-3 text-sm font-semibold text-black hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={safePage === 1 || isLoading}
          >
            Previous
          </button>
          <span aria-live="polite">
            Page {safePage} of {totalPages}
          </span>
          <button
            type="button"
            className="h-8 border border-cool-gray-7 px-3 text-sm font-semibold text-black hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            disabled={safePage === totalPages || isLoading}
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
}